import { Avatar } from '@trustoo/components/Avatar/v2'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef } from 'react'

export type AvatarGroupSize = 'xs' | 'sm' | 'md'

export interface AvatarGroupItem {
    src?: string
    alt?: string
    initials?: string
}

export interface AvatarGroupProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
    items: AvatarGroupItem[]
    size?: AvatarGroupSize
    max?: number
    addMoreButton?: boolean
    addMoreDisabled?: boolean
    onAddMoreClick?: () => void
    addMoreLabel?: string
}

const STACK_OVERLAP_CLASSES: Record<AvatarGroupSize, string> = {
    xs: '-space-x-spacing-xs',
    sm: '-space-x-spacing-md',
    md: '-space-x-spacing-lg',
}

const COIN_SIZE_CLASSES: Record<AvatarGroupSize, string> = {
    xs: 'size-6',
    sm: 'size-8',
    md: 'size-10',
}

const OVERFLOW_TEXT_CLASSES: Record<AvatarGroupSize, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-md',
}

const PLUS_ICON_CLASSES: Record<AvatarGroupSize, string> = {
    xs: 'size-4',
    sm: 'size-4',
    md: 'size-5',
}

function PlusIcon({ size }: { size: AvatarGroupSize }) {
    return (
        <svg
            className={PLUS_ICON_CLASSES[size]}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
        </svg>
    )
}

export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
    (
        {
            items,
            size = 'md',
            max,
            addMoreButton = false,
            addMoreDisabled = false,
            onAddMoreClick,
            addMoreLabel = 'Add user',
            className,
            ...rest
        },
        ref,
    ) => {
        const hasOverflow = max !== undefined && max >= 0 && items.length > max
        const visibleItems = hasOverflow ? items.slice(0, max) : items
        const overflowCount = items.length - visibleItems.length

        return (
            <div
                ref={ref}
                role="group"
                className={clsx('inline-flex items-center gap-spacing-md', className)}
                {...rest}>
                <div className={clsx('flex items-center', STACK_OVERLAP_CLASSES[size])}>
                    {visibleItems.map((item, index) => (
                        <span
                            key={`${item.src ?? item.initials ?? 'avatar'}-${index}`}
                            className="relative inline-flex rounded-radius-full ring-[1.5px] ring-background-bg-primary">
                            <Avatar
                                size={size}
                                src={item.src}
                                alt={item.alt}
                                initials={item.initials}
                            />
                        </span>
                    ))}

                    {hasOverflow && (
                        <span
                            role="img"
                            aria-label={`${overflowCount} more`}
                            className={clsx(
                                'relative inline-flex shrink-0 items-center justify-center rounded-radius-full',
                                'border-[0.5px] border-solid border-border-secondary bg-background-bg-tertiary',
                                'ring-[1.5px] ring-background-bg-primary',
                                'font-body font-semibold text-text-quaternary',
                                COIN_SIZE_CLASSES[size],
                                OVERFLOW_TEXT_CLASSES[size],
                            )}>
                            +{overflowCount}
                        </span>
                    )}
                </div>

                {addMoreButton && (
                    <button
                        type="button"
                        onClick={onAddMoreClick}
                        disabled={addMoreDisabled}
                        aria-label={addMoreLabel}
                        title={addMoreLabel}
                        className={clsx(
                            'inline-flex shrink-0 items-center justify-center rounded-radius-full p-spacing-xs',
                            'border border-dashed border-border-primary bg-background-bg-primary text-foreground-fg-quaternary',
                            'transition-colors duration-100 ease-out',
                            'enabled:hover:bg-background-bg-primary_hover enabled:hover:text-foreground-fg-quaternary_hover',
                            'focus-visible:border-border-brand focus-visible:outline-none',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            COIN_SIZE_CLASSES[size],
                        )}>
                        <PlusIcon size={size} />
                    </button>
                )}
            </div>
        )
    },
)

AvatarGroup.displayName = 'AvatarGroup'
